/* import __COLOCATED_TEMPLATE__ from './row-actions.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import type AppService from 'embercom/services/app-service';
import type Store from '@ember-data/store';
import type { KnowledgeHubTableRow } from 'embercom/models/content-service/knowledge-hub-content-wrapper';
import { EntityType } from 'embercom/models/data/entity-types';
import { taskFor } from 'ember-concurrency-ts';
import { dropTask } from 'ember-concurrency-decorators';
import type { TaskGenerator } from 'ember-concurrency';
import { SyncStatus } from 'embercom/models/knowledge-hub/sync-source-wrapper';
import { ResyncType } from 'embercom/models/content-service/content-import-source';
import { ajaxDelete } from 'embercom/lib/ajax';
import { type ListApi } from 'embercom/components/knowledge-hub/filterable-list/list-row';

interface Signature {
  Args: {
    tableRow: KnowledgeHubTableRow;
    activeHoverRowId?: string | null;
    onResyncTriggered?: () => void;
    listApi?: ListApi;
  };
}

export default class RowActions extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare notificationsService: any;
  @tracked isDropdownOpen = false;
  @tracked showDeleteConfirmModal = false;

  get isSyncableContent(): boolean {
    let tableRow = this.args.tableRow;
    let entityType = 'entityType' in tableRow ? tableRow.entityType : tableRow.contentType;
    return (
      entityType === EntityType.ExternalContent || entityType === EntityType.ContentImportRunRecord
    );
  }

  get shouldShowActions(): boolean {
    // Only show actions for syncable content when hovering over this specific row OR when dropdown is open
    let isHoveringOrOpen =
      this.args.activeHoverRowId === this.args.tableRow.id || this.isDropdownOpen;

    if (!this.isSyncableContent || !isHoveringOrOpen) {
      return false;
    }

    return true;
  }

  get isFilteredOrExcluded(): boolean {
    let tableRow = this.args.tableRow as any;
    let syncStatus = tableRow.syncStatus?.syncStatus;
    return syncStatus === SyncStatus.FILTERED || syncStatus === SyncStatus.EXCLUDED;
  }

  private getFolder() {
    let tableRow = this.args.tableRow;
    let folderId = (tableRow as any).relations?.folder?.folder_id;
    if (!folderId) {
      return null;
    }
    return this.store.peekRecord('content-service/folder', folderId);
  }

  private getSyncSource(folder: any) {
    if (!folder) {
      return null;
    }
    let syncSources = this.store.peekAll('knowledge-hub/sync-source-wrapper');
    return syncSources.find((source) => {
      return source.folderId && source.folderId.toString() === folder.id.toString();
    });
  }

  get isSyncing(): boolean {
    let folder = this.getFolder();
    let syncSource = this.getSyncSource(folder);
    return (
      syncSource?.isSyncing ||
      taskFor(this.resyncTask).isRunning ||
      taskFor(this.deleteFromSyncTask).isRunning
    );
  }

  get actionOptions() {
    let items = [];

    if (this.isFilteredOrExcluded) {
      items.push({
        text: this.intl.t('knowledge-hub.filterable-list.row-actions.delete-from-sync'),
        icon: 'trash',
        isDestructive: true,
        isDisabled: this.isSyncing,
        tooltipText: this.isSyncing
          ? this.intl.t(
              'knowledge-hub.overview.sections.syncs.rows.active.action.options.disabled-delete-tooltip',
            )
          : undefined,
        tooltip: { isDelayed: false },
        onSelectItem: this.showDeleteConfirmationModal,
      });
    } else {
      items.push({
        text: this.intl.t('knowledge-hub.filterable-list.row-actions.resync'),
        icon: 'sync',
        isDisabled: this.isSyncing,
        tooltipText: this.isSyncing
          ? this.intl.t(
              'knowledge-hub.overview.sections.syncs.rows.active.action.options.disabled-resync-tooltip',
            )
          : undefined,
        tooltip: { isDelayed: false },
        onSelectItem: () => {
          if (!this.isSyncing) {
            taskFor(this.resyncTask).perform();
          }
        },
      });
    }

    return [{ items }];
  }

  @action
  handleDropdownOpen(): void {
    this.isDropdownOpen = true;
  }

  @action
  handleDropdownClose(): void {
    this.isDropdownOpen = false;
  }

  @action
  showDeleteConfirmationModal(): void {
    if (!this.isSyncing) {
      this.showDeleteConfirmModal = true;
    }
  }

  private updateSyncStatusCounts(): void {
    let folder = this.getFolder();
    if (!folder?.contentImportSourceId) {
      return;
    }

    let contentImportSource = this.store.peekRecord(
      'content-service/content-import-source',
      folder.contentImportSourceId,
    );

    if (!contentImportSource?.statusCounts) {
      return;
    }

    if (contentImportSource.statusCounts.total) {
      contentImportSource.statusCounts.total--;
    }

    // Delete action only appears for filtered/excluded items (see isFilteredOrExcluded getter)
    if (contentImportSource.statusCounts.excluded) {
      contentImportSource.statusCounts.excluded--;
    }

    contentImportSource.notifyPropertyChange('statusCounts');
  }

  @dropTask
  *resyncTask(): TaskGenerator<void> {
    let tableRow = this.args.tableRow;

    try {
      // Expected format: "{app_id}_{content_wrapper_id}" (e.g., "8_165_15417" -> "165_15417")
      let fullId = String(tableRow.id);
      let idParts = fullId.split('_');

      if (idParts.length < 2) {
        this.notificationsService.notifyError(
          this.intl.t('knowledge-hub.filterable-list.sources.actions.resync-error'),
        );
        return;
      }

      let contentWrapperId = idParts.slice(1).join('_');

      let folder = this.getFolder();
      if (!folder) {
        this.notificationsService.notifyError(
          this.intl.t('knowledge-hub.filterable-list.sources.actions.resync-error'),
        );
        return;
      }

      let syncSource = this.getSyncSource(folder);
      if (syncSource) {
        syncSource.status = SyncStatus.SYNCING;
      }

      let sourceId = folder.contentImportSourceId;
      if (!sourceId) {
        this.notificationsService.notifyError(
          this.intl.t('knowledge-hub.filterable-list.sources.actions.resync-error'),
        );
        return;
      }

      let contentImportSource = this.store.peekRecord(
        'content-service/content-import-source',
        sourceId,
      );

      if (!contentImportSource) {
        this.notificationsService.notifyError(
          this.intl.t('knowledge-hub.filterable-list.sources.actions.resync-error'),
        );
        return;
      }

      yield contentImportSource.triggerResync([contentWrapperId], ResyncType.SELECTIVE);

      if (this.args.onResyncTriggered) {
        this.args.onResyncTriggered();
      }
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.filterable-list.sources.actions.resync-error'),
      );
    }
  }

  @dropTask
  *deleteFromSyncTask(): TaskGenerator<void> {
    let tableRow = this.args.tableRow;

    try {
      this.showDeleteConfirmModal = false;

      // Expected format: "{content_wrapper_type}_{entity_type}_{entity_id}"
      let fullId = String(tableRow.id);
      let idParts = fullId.split('_');

      if (idParts.length < 2) {
        this.notificationsService.notifyError(
          this.intl.t('knowledge-hub.filterable-list.row-actions.delete-from-sync-error'),
        );
        return;
      }

      let contentImportRunRecordId = idParts[idParts.length - 1];
      let appId = this.appService.app.id;
      yield ajaxDelete(
        `/ember/content_service/content_import_run_records/${contentImportRunRecordId}?app_id=${appId}`,
      );

      this.notificationsService.notifyConfirmation(
        this.intl.t('knowledge-hub.filterable-list.row-actions.delete-from-sync-success'),
      );

      if (this.args.listApi) {
        this.args.listApi.removeContentWrapper(tableRow.id);
        this.updateSyncStatusCounts();
      }
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.filterable-list.row-actions.delete-from-sync-error'),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::FilterableList::Table::Cells::RowActions': typeof RowActions;
    'knowledge-hub/filterable-list/table/cells/row-actions': typeof RowActions;
  }
}
